import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Bio } from '../../data/constants';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(133, 76, 230, 0.4); }
  50% { box-shadow: 0 0 0 12px rgba(133, 76, 230, 0); }
`;

const Container = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
  }
`;

const Tooltip = styled(motion.div)`
  background: #1a1a2e;
  color: #2196F3;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  
  &::after {
    content: '';
    position: absolute;
    right: 22px;
    bottom: -8px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #1a1a2e;
  }
`;

const MainButton = styled(motion.button)`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 20px rgba(33, 150, 243, 0.4);
  animation: ${pulse} 2s infinite;
  
  &:hover {
    animation: none;
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(33, 150, 243, 0.5);
  }
  
  svg {
    font-size: 28px;
  }
`;

const SocialIconsContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px;
  background: rgba(26, 26, 46, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  border: 1px solid rgba(133, 76, 230, 0.2);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
`;

const SocialIcon = styled(motion.a)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  background: ${({ $bgColor }) => $bgColor || '#333'};
  text-decoration: none;
  
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 0 20px ${({ $bgColor }) => $bgColor || '#333'}80;
  }
  
  svg {
    font-size: 24px;
  }
`;

const CloseButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-top: 4px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    font-size: 20px;
  }
`;

const socialLinks = [
  { icon: <TwitterIcon />, url: Bio.twitter, color: '#1DA1F2', name: 'Twitter' },
  { icon: <InstagramIcon />, url: Bio.insta, color: '#E4405F', name: 'Instagram' },
  { icon: <GitHubIcon />, url: Bio.github, color: '#333', name: 'GitHub' },
  { icon: <LinkedInIcon />, url: Bio.linkedin, color: '#0A66C2', name: 'LinkedIn' },
  { icon: <WhatsAppIcon />, url: `https://wa.me/${Bio.whatsapp || ''}`, color: '#25D366', name: 'WhatsApp' },
];

const containerVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: 0.2 },
  },
};

const iconVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.8 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 300, damping: 20 },
  },
};

const SocialSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setShowTooltip(false);
  };

  return (
    <Container>
      <AnimatePresence>
        {!isOpen && showTooltip && (
          <Tooltip
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
          >
            Let's connect!
          </Tooltip>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <SocialIconsContainer
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {socialLinks.map((social, index) => (
              <SocialIcon
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                $bgColor={social.color}
                variants={iconVariants}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
                title={social.name}
              >
                {social.icon}
              </SocialIcon>
            ))}
            <CloseButton
              onClick={handleToggle}
              variants={iconVariants}
              whileHover={{ rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <CloseIcon />
            </CloseButton>
          </SocialIconsContainer>
        )}
      </AnimatePresence>

      {!isOpen && (
        <MainButton
          onClick={handleToggle}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <PersonAddIcon />
        </MainButton>
      )}
    </Container>
  );
};

export default SocialSidebar;
